import {
  TerminalClaudeModel,
  TerminalPermissionMode,
  TerminalSessionStatus,
} from '@this+that/models';

import { TerminalSlashCommand } from '../../enums/terminal';

export const TERMINAL_DEFAULT_HEIGHT_PX = 470;
export const TERMINAL_MIN_HEIGHT_PX = 240;
export const TERMINAL_MAX_HEIGHT_VIEWPORT_PCT = 85;
export const TERMINAL_HEIGHT_KEY_STEP_PX = 24;
export const TERMINAL_HEIGHT_STORAGE_PREFIX = 'terminal.height.';
export const TERMINAL_DEVICE_STORAGE_PREFIX = 'terminal.device.';
export const TERMINAL_ANONYMOUS_USER_KEY = 'anonymous';

export const TERMINAL_DEVICE_PICKER_TEST_IDS = {
  trigger: 'terminal-device-picker',
  automatic: 'terminal-device-picker-option-automatic',
  option: 'terminal-device-picker-option',
  active: 'terminal-device-picker-active',
} as const;

export const TERMINAL_SHORTCUT_KEY = '`';
export const TERMINAL_SHORTCUT_CODE = 'Backquote';
export const TERMINAL_SHORTCUT_KEYS_ATTR = 'Control+`';
export const TERMINAL_SHORTCUT_LABEL = '⌃`';

export const TERMINAL_SHELL_PREFIX = '!';
export const TERMINAL_SLASH_PREFIX = '/';
export const TERMINAL_MENTION_TRIGGER = '@';
export const TERMINAL_MENTION_PATTERN = /(^|\s)@([^\s@]*)$/;
export const TERMINAL_MENTION_MAX_RESULTS = 6;
export const TERMINAL_MENTION_BODY_MAX_CHARS = 2000;

export const TERMINAL_INPUT_MAX_LINES = 6;
export const TERMINAL_INPUT_LINE_HEIGHT_PX = 20;
export const TERMINAL_HISTORY_MAX_ENTRIES = 200;

export const TERMINAL_LOG_PIN_THRESHOLD_PX = 24;
export const TERMINAL_OUTPUT_MAX_LINES = 40;
export const TERMINAL_OUTPUT_LINE_SPLIT = '\n';

export const TERMINAL_GIT_STATUS_COMMAND_PATTERN = /^\s*git\s+status\b/;
export const TERMINAL_GIT_STATUS_LINE_PATTERN =
  /^(\s?(?:\?\?|[MADRCU!]{1,2}))(\s+)(.*)$/;

export const TERMINAL_ALWAYS_ALLOW_FALLBACK = 'command';

export const TERMINAL_SLASH_COMMANDS: readonly TerminalSlashCommand[] = [
  TerminalSlashCommand.CD,
  TerminalSlashCommand.CLEAR,
  TerminalSlashCommand.NEW,
  TerminalSlashCommand.HELP,
  TerminalSlashCommand.MOVE,
  TerminalSlashCommand.ATTACH,
  TerminalSlashCommand.MODEL,
];

export const TERMINAL_SLASH_COMMANDS_WITH_ARGUMENT: readonly TerminalSlashCommand[] =
  [TerminalSlashCommand.CD];

export const TERMINAL_PERMISSION_MODES: readonly TerminalPermissionMode[] = [
  TerminalPermissionMode.ASK,
  TerminalPermissionMode.ACCEPT_EDITS,
  TerminalPermissionMode.BYPASS,
];

export const TERMINAL_CLAUDE_MODELS: readonly TerminalClaudeModel[] = [
  TerminalClaudeModel.DEFAULT,
  TerminalClaudeModel.OPUS,
  TerminalClaudeModel.SONNET,
  TerminalClaudeModel.HAIKU,
];

export const TERMINAL_BUSY_STATUSES: readonly TerminalSessionStatus[] = [
  TerminalSessionStatus.RUNNING,
  TerminalSessionStatus.WAITING_PERMISSION,
];

export const TERMINAL_MOCK_LATENCY_MS = 120;
export const TERMINAL_MOCK_CHUNK_INTERVAL_MS = 180;
export const TERMINAL_MOCK_MACHINE_NAME = 'Mac.localdomain';

export const TERMINAL_LOCAL_HTTP_BASE = 'http://127.0.0.1:8788';
export const TERMINAL_LOCAL_WS_BASE = 'ws://127.0.0.1:8788';
export const TERMINAL_LOCAL_PING_PATH = '/terminal/ping';
export const TERMINAL_LOCAL_SOCKET_PATH = '/terminal';
export const TERMINAL_LOCAL_PING_TIMEOUT_MS = 1500;
export const TERMINAL_LOCAL_MAX_FAILURES_BEFORE_OFFLINE = 3;
export const TERMINAL_BACKOFF_BASE_MS = 1000;
export const TERMINAL_BACKOFF_MAX_MS = 30_000;
export const TERMINAL_BACKOFF_FACTOR = 2;
export const TERMINAL_ERROR_CODE_SEND_FAILED = 'send_failed';
export const TERMINAL_ERROR_CODE_TOO_LARGE = 'message_too_large';
export const TERMINAL_ERROR_CODE_INVALID = 'message_invalid';
export const TERMINAL_RELAY_KEEPALIVE_MS = 4 * 60 * 1000;
export const TERMINAL_LOCAL_AUTH_FRAME_TYPE = 'auth';

export const TERMINAL_ROOT_SELECTOR = '[data-terminal-root="true"]';
export const TERMINAL_CLICK_IGNORE_SELECTOR =
  'button, a, input, textarea, select, summary, label, [role="tab"], [role="menu"], [role="menuitem"], [role="menuitemoption"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="option"], [role="separator"], [role="combobox"], [role="dialog"], [role="checkbox"], [role="radio"], .chakra-popover__content, .chakra-popover__popper, [data-popper-placement]';
export const TERMINAL_ANSWER_COLLAPSED_MAX_HEIGHT_CLASS = 'max-h-[800px]';
export const TERMINAL_SESSIONS_GRACE_MS = 1500;
export const TERMINAL_SESSIONS_LOAD_TIMEOUT_MS = 5000;
export const TERMINAL_SESSION_FOCUS_WAIT_MS = 15_000;
export const TERMINAL_HOLD_ABSENCE_GRACE_MS = 4000;

export const TERMINAL_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const TERMINAL_ATTACHMENT_MAX_PER_MESSAGE = 5;
export const TERMINAL_ATTACHMENT_ALLOWED_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/markdown',
  'application/json',
  'text/csv',
]);
export const TERMINAL_ATTACHMENT_EXTENSION_TYPES: Readonly<
  Record<string, string>
> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  json: 'application/json',
  csv: 'text/csv',
};
export const TERMINAL_ATTACHMENT_IMAGE_PREFIX = 'image/';
export const TERMINAL_ATTACHMENT_ACCEPT = Object.keys(
  TERMINAL_ATTACHMENT_EXTENSION_TYPES,
)
  .map((extension) => `.${extension}`)
  .join(',');
export const TERMINAL_FOLDER_PICK_ERROR_CODES: readonly string[] = [
  'folder_pick_cancelled',
  'folder_not_git_repository',
  'folder_pick_in_progress',
];
export const TERMINAL_ATTACHMENT_REJECTED_CODE = 'attachment_rejected';

export const TERMINAL_MOVE_UNDO_TOAST_MS = 6000;
export const TERMINAL_MOVE_HIGHLIGHT_MS = 2000;
export const TERMINAL_DEFAULT_COLUMN_TITLE = 'in progress';
export const TERMINAL_FINISH_ENABLED = true;
export const TERMINAL_ENTRY_GAP_MS = 1500;
